import { formatChemicalCompoundNameAsUnicode } from "@omnia/shared/utils/format";
import { ICropping, IOmniaShell, isNumber } from "./omnia"
import { INutrient, INutrientGroup, INutritionAppQty, INutritionPlanOrder, INutritionPlanProductApp, INutritionProductApp, INutritionService, IPlanEditArguments, ISoilTypeService, NutrientType } from "./types"

import { isDate } from "@omnia/shared/utils/utils";
import { SoilCategory, SubsoilTypeId } from "@omnia/shared/generated/enums";
import { showWarning } from "@omnia/shared/shell/msgbox/msgbox";
import { escape } from "@omnia/shared/utils/regexp";

export const DefaultNeutralisingValue = 50;
export const LimeEnabledFromYear = 2018;

export function getPlanPillText(name: string): string {

    if (name) {

        var words = name.split(/[\s / -\.:;]+/);
        if (words.length > 3) {
            words = words.slice(0, 3);
        }

        return words.map(w => w.substring(0, 1).toLocaleUpperCase()).join("");

    }

}

export function getNutrientTypes(): NutrientType[] {
    return [
        NutrientType.N,
        NutrientType.SO3,
        NutrientType.P2O5,
        NutrientType.K2O,
        NutrientType.MgO,
        NutrientType.Lime,
    ];
}

export function getNutrientKeys(): string[] {
    return [
        "N",
        "SO3",
        "P2O5",
        "K2O",
        "MgO",
        "Lime",
    ];
}

export function getNutrientGroups(includeLime: boolean, prefix?: string): { nutrients: INutrient[], groups: INutrientGroup[] } {

    let nutrients = ["N", "SO3", "P2O5", "K2O", "MgO", "Lime"].map(k => {
        return {
            key: k,
            name: formatChemicalCompoundNameAsUnicode(k),
            nutrient: NutrientType[k],
            tonnesPerHa: false
        }
    });

    let groupDefs = [
        {
            key: "*",
            name: "All nutrients",
            nutrients: ["N", "SO3", "P2O5", "K2O", "MgO"].concat(includeLime ? ["Lime"] : [])
        },
        {
            key: "NS",
            name: "N and S",
            nutrients: ["N", "SO3"]
        },
        {
            key: "PK",
            name: "P, K and MgO",
            nutrients: ["P2O5", "K2O", "MgO"]
        },

    ];

    if (includeLime) {

        groupDefs.push(
            {
                key: "Lime",
                name: "Lime",
                nutrients: ["Lime"]
            }
        );

    }

    let groups = groupDefs.map(g => {

        let available = g.nutrients ? g.nutrients.map(k => nutrients.find(n => n.key == k)) : nutrients;

        return {
            key: g.key,
            name: formatChemicalCompoundNameAsUnicode(g.name),
            nutrients: available,
        }

    })

    if (prefix) {
        nutrients.forEach(n => n.key = prefix + n.key);
    }

    let results = {
        nutrients: nutrients,
        groups: groups
    }

    return results;

}

export function comparePlansOrder(a: INutritionPlanOrder, b: INutritionPlanOrder): number {

    let d = a.HoldingId - b.HoldingId
    if (d) return d

    d = a.YearId - b.YearId
    if (d) return d

    d = (a.CalculationOrder || 0) - (b.CalculationOrder || 0)
    if (d) return d

    d = a.CreatedOn.valueOf() - b.CreatedOn.valueOf()
    if (d) return d

    return a.NutritionPlanId - b.NutritionPlanId

}

export function sortPlansByCalcOrder<T>(items: T[], selector: (item: T) => INutritionPlanOrder): T[] {

    return items
        .map(i => ({ item: i, order: selector(i) }))
        .sort((a, b) => comparePlansOrder(a.order, b.order))
        .map(i => i.item)

}

export function applyMinMaxRates(rate: number, app: INutritionAppQty) {

    if (rate < app.CutOffProductRate) {
        rate = 0
    }
    else if (isNumber(app.MinProductRate) && (rate < app.MinProductRate)) {
        rate = app.MinProductRate
    }

    if (app.MaxProductRate && (rate > app.MaxProductRate)) {
        rate = app.MaxProductRate
    }

    return rate

}

export function sortReferenceDates(a1: INutritionProductApp, a2: INutritionProductApp): number {

    var d1 = isDate(a1.referenceDate);
    var d2 = isDate(a2.referenceDate);
    if (d1 != d2) return (d1 ? 0 : 1) - (d2 ? 0 : 1);
    if (d1 && d2) return a1.referenceDate.valueOf() - a2.referenceDate.valueOf();

}

export function sortApplicationDates(a1: INutritionPlanProductApp, a2: INutritionPlanProductApp): number {

    var a1Ref = a1.ActualDate || a1.ApplicationDate;
    var a2Ref = a2.ActualDate || a2.ApplicationDate;

    var d1 = isDate(a1Ref);
    var d2 = isDate(a2Ref);
    if (d1 != d2) return (d1 ? 0 : 1) - (d2 ? 0 : 1);
    if (d1 && d2) return a1Ref.valueOf() - a2Ref.valueOf();

}

export function checkLightOrShallowSoil(svc: ISoilTypeService, cropping: ICropping): boolean {

    switch (cropping.Field.SubsoilTypeId) {

        case SubsoilTypeId.Shallow:
        case SubsoilTypeId.ShallowOverSandstone:
            return true

        default:
            const cls = svc.getSoilClassification(cropping.Field.SoilTypeId, cropping.Field.SubsoilTypeId)
            return (cls == SoilCategory.Light)

    }
}


export function getExclusiveAccess(svc: INutritionService, shell: IOmniaShell, continueReadOnly: boolean): ng.IPromise<() => ng.IPromise<any>> {

    return svc.getExclusiveAccess().then(ok => {

        const release = () => svc.releaseExclusiveAccess()

        if (ok) {
            return release
        }

        const content = [
            "<p>This action cannot be completed because another user is currently doing conflicting changes on this holding.</p>",
            continueReadOnly
                ? "<p>The screen will open in read-only mode so that you can review the data. Please try closing the screen and opening it again later to get read-write access.</p>"
                : undefined
        ].filter(l => !!l).join("")

        return showWarning(shell, content).then(_ => undefined)

    })

}

export function editPlan(svc: INutritionService, shell: IOmniaShell, args: IPlanEditArguments): ng.IPromise<boolean> {

    const newPlan = !args.plan

    return getExclusiveAccess(svc, shell, !newPlan)
        .then(release => {

            if (!release) {

                if (newPlan) {
                    return false
                }

                args = {
                    ...args,
                    readOnly: true
                }

            }

            const dialog = shell.showComponentAsDialog("omnia-nutri-plan-edit", args, {
                size: "full-screen",
                closeOnEscape: false,
                backdrop: "static"
            })

            return dialog.then(_ => release
                ? release().then(_ => false)
                : false
            )

        })


}

export function calculateRequirements(svc: INutritionService, shell: IOmniaShell, croppingIds: number[]): ng.IPromise<boolean> {

    return getExclusiveAccess(svc, shell, false)
        .then(release => {

            if (!release) {
                return false
            }

            const args = croppingIds

            const dialog = shell.showComponentAsDialog("omnia-nutri-requirements-config", args, {
                size: "xlarge",
                closeOnEscape: false,
                backdrop: "static"
            })

            return dialog.then(release).then(_ => true)

        })

}

export function applyPlanFilter<T>(plans: T[], planInfoSelector: (plan: T) => { name: string, fields: string[], crops: string[] }, filter: string): T[] {

    if (!filter) {
        return plans
    }

    const re = new RegExp(escape(filter), "i")
    const test = (v: string) => re.test(v)

    return plans.filter(p => {
        const i = planInfoSelector(p)
        return (test(i.name) || i.fields.some(test) || i.crops.some(test))
    })

}
